// ============================================================
// AttackerOverviewTab - Overview tab of the Attacker panel.
// Renders the harmonized OverviewSheet (v6.33.0): difficulty →
// attributes+movement → dice tray → skills → combat rules →
// active talents → traits. (Effective combat stats and the
// weapon profile live in EffectiveProfileCard on Controls.)
//
// Condition modifiers apply here too (v7.3.1): the acting character's
// own Stunned/Fatigued/custom penalties shift their own tests.
// ============================================================
import { memo } from 'react';
import styles from '../styles/AttackerPanel.module.css';
import overview from '../styles/OverviewSheet.module.css';
import OverviewSheet from './overview/OverviewSheet';
import DiceTray from './DiceTray';
import { useAttacker } from '../contexts/AttackerContext';
import { useSession } from '../contexts/SessionContext';
import {
  resolveApplyConditionModifiers,
  resolveConditionModOptions,
  resolveConditionsBlockSheetTests,
} from '../engine/settings/resolve';
import { blockedReactionTests } from '../engine/reactionAvailability';

function AttackerOverviewTab() {
  const {
    attackerSourceCharacter,
    rollCharacteristic,
    rollSkill,
    rollDiceExpr,
    rollOpposedCharacteristic,
    rollOpposedSkill,
    unnaturalOverrides,
    onUnnaturalOverride,
    actorTarget,
  } = useAttacker();
  const { permissions } = useSession();
  const applyConditionModifiers = resolveApplyConditionModifiers(permissions);
  const condModOptions = resolveConditionModOptions(permissions);
  // Tests a condition forbids outright, resolved where the permissions
  // are. The leaves render the map; they do not re-derive the rule.
  const blockedTests = blockedReactionTests(actorTarget, {
    applyConditionMods: applyConditionModifiers,
    condOptions: condModOptions,
    blockSheetTests: resolveConditionsBlockSheetTests(permissions),
  });

  if (!attackerSourceCharacter) return <NoCharacterOverview rollDiceExpr={rollDiceExpr} />;

  return (
    <div className={styles.tabContent}>
      <OverviewSheet
        character={attackerSourceCharacter}
        onRollCharacteristic={rollCharacteristic}
        onRollSkill={rollSkill}
        onRollDiceExpr={rollDiceExpr}
        onOpposedCharacteristic={rollOpposedCharacteristic}
        onOpposedSkill={rollOpposedSkill}
        unnaturalOverrides={unnaturalOverrides}
        onUnnaturalOverride={onUnnaturalOverride}
        /* v7.3.1 - the acting character's OWN conditions modify their own
           tests, exactly as the target side already did. OverviewSheet
           needs a projected Target to read them from; without it the two
           sides silently disagreed about the same person. */
        target={actorTarget}
        applyConditionModifiers={applyConditionModifiers}
        condModOptions={condModOptions}
        blockedTests={blockedTests}
      />
    </div>
  );
}

/** No character loaded: say so, and keep the free-form dice tray working. */
function NoCharacterOverview({ rollDiceExpr }) {
  return (
    <div className={styles.tabContent}>
      <div className={styles.overviewEmpty}>
        No character loaded. Load a character from the Library to roll characteristic and skill tests here.
      </div>
      <div className={overview.card}>
        <div className={overview.cardTitle}>Dice Tray</div>
        <DiceTray onRollExpr={rollDiceExpr} />
      </div>
    </div>
  );
}

export default memo(AttackerOverviewTab);
